/**
 * Shared signup — Preferred Payment Method page/company/location routing.
 *
 * Used by both Foodbuy and HSSI (sections/foodbuy-company.liquid,
 * sections/hssi-company.liquid). The page id, registration hash key, and
 * company/location hidden fields (page_ID, company_ID, location_ID,
 * company_name, company_location_name, external_ID, payment_term, and the
 * tags field — read by company-register-v2.js's
 * collectFormDataAsJSON()/submitFormToAPI()) no longer default to the
 * page's own id/metafields. Instead they're filled from one of two
 * dedicated pages per brand, rendered server-side by each section as data
 * attributes on the shared #payment-method-metadata element:
 *  - Foodbuy: /pages/foodbuy-cc (Credit Card), /pages/foodbuy-account-create (Payment Terms)
 *  - HSSI:    /pages/hssi-cc-account-create (Credit Card), /pages/hssi-account-create (Payment Terms)
 *
 * The Preferred Payment Method radios (snippets/signup-payment-method.liquid)
 * are shared with other partner signup pages; this file only activates when
 * both the radios and #payment-method-metadata are present on the page, so
 * it is safe to load from any partner section.
 */

"use strict";

const PAYMENT_METHOD_RADIO_SELECTOR = 'input[name="customer[payment_method]"]';
const PAYMENT_METHOD_METADATA_ELEMENT_ID = "payment-method-metadata";
const PAYMENT_METHOD_HASH_ELEMENT_ID = "customer-registration-hash";
const PAYMENT_METHOD_CREDIT_CARD_VALUE = "Credit Card";
const PAYMENT_METHOD_PAYMENT_TERMS_VALUE = "Payment Terms";

/**
 * Field selector -> metadata dataset key suffix (see the file comment
 * above). The tags field is targeted by its `name`, not `id="company"` —
 * that id collides with the visible Company field on some partner pages,
 * and getElementById() would otherwise silently hit the wrong input.
 */
const PAYMENT_METHOD_FIELD_TO_DATASET_SUFFIX = {
  "#page_ID": "PageId",
  "#company_ID": "LastCompanyId",
  "#location_ID": "LocationId",
  "#company_name": "CompanyName",
  "#company_location_name": "CompanyLocationName",
  "#external_ID": "ExternalId",
  "#payment_term": "PaymentTerm",
  'input[name="customer[tags]"]': "Tags",
};

/**
 * Read the company/location values rendered server-side for one payment
 * option ("creditCard" or "paymentTerms" dataset key prefix).
 *
 * @param {string} prefix
 * @returns {Object<string, string>|null} selector -> value, or null when the
 *          metadata element itself is missing from the page.
 */
function paymentMethodReadMetadata(prefix) {
  const metaElement = document.getElementById(PAYMENT_METHOD_METADATA_ELEMENT_ID);
  if (null === metaElement) return null;

  const values = {};
  Object.entries(PAYMENT_METHOD_FIELD_TO_DATASET_SUFFIX).forEach(([selector, suffix]) => {
    values[selector] = metaElement.dataset[`${prefix}${suffix}`] ?? "";
  });
  return values;
}

/**
 * @param {Object<string, string>} fieldValues - selector -> value.
 */
function paymentMethodApplyFields(fieldValues) {
  Object.entries(fieldValues).forEach(([selector, value]) => {
    const field = document.querySelector(selector);
    if (field) field.value = value;
  });
}

/**
 * Switch #customer-registration-hash's pageId and hashKey to the selected payment
 * option's page — together, since the sector-details lookup (company-signup-lookup.js)
 * sends them as one id+key pair and the key is only valid for the pageId it was
 * computed from.
 *
 * If the dedicated page for this option wasn't found server-side (e.g. the
 * /pages/foodbuy-cc or /pages/hssi-cc-account-create page doesn't exist/isn't
 * published yet), pageId/hashKey come through blank. Overwriting the hash element
 * with blanks would silently break the DCN/MARSHA lookup with no error at all, so
 * in that case this leaves the current pageId/hashKey (the page's own) in place
 * instead, and logs a warning so the missing page is easy to spot.
 *
 * @param {string|undefined} pageId
 * @param {string|undefined} hashKey
 */
function paymentMethodSyncHash(pageId, hashKey) {
  const hashElement = document.getElementById(PAYMENT_METHOD_HASH_ELEMENT_ID);
  if (!hashElement) return;

  if (!pageId || !hashKey) {
    console.warn(
      "[payment-method-routing] Missing pageId/hashKey for the selected payment method — " +
      "is its dedicated page published? Leaving the current sector-details lookup pageId/hashKey unchanged."
    );
    return;
  }

  hashElement.dataset.pageId = pageId;
  hashElement.dataset.hashKey = hashKey;
}

/**
 * Wire the Preferred Payment Method radios so the company/location fields
 * follow whichever page the selected option maps to.
 */
function paymentMethodInitRouting() {
  const radios = document.querySelectorAll(PAYMENT_METHOD_RADIO_SELECTOR);
  const metaElement = document.getElementById(PAYMENT_METHOD_METADATA_ELEMENT_ID);
  if (!radios.length || null === metaElement) return;

  const creditCardValues = paymentMethodReadMetadata("creditCard");
  const paymentTermsValues = paymentMethodReadMetadata("paymentTerms");

  function syncPaymentMethodFields() {
    const checked = Array.from(radios).find((radio) => radio.checked && !radio.disabled);
    if (!checked) return;

    if (checked.value === PAYMENT_METHOD_CREDIT_CARD_VALUE && creditCardValues) {
      paymentMethodApplyFields(creditCardValues);
      paymentMethodSyncHash(metaElement.dataset.creditCardPageId, metaElement.dataset.creditCardHashKey);
    } else if (checked.value === PAYMENT_METHOD_PAYMENT_TERMS_VALUE && paymentTermsValues) {
      paymentMethodApplyFields(paymentTermsValues);
      paymentMethodSyncHash(metaElement.dataset.paymentTermsPageId, metaElement.dataset.paymentTermsHashKey);
    }
  }

  radios.forEach((radio) => radio.addEventListener("change", syncPaymentMethodFields));
}

document.addEventListener("DOMContentLoaded", paymentMethodInitRouting);
